/**
 * useTrainingPhase — Derives the current mesocycle phase from volume proximity
 *
 * Phase logic (4 phases, like Fitbod):
 *   Phase 1 — Foundation      (0–40% of MRV)   "Build the base"
 *   Phase 2 — Development     (40–70% of MRV)  "Progressive overload in effect"
 *   Phase 3 — Peak Effort     (70–90% of MRV)  "Close to peak — push hard"
 *   Phase 4 — Overreach/Deload (>90% or deload flag) "Recovery week incoming"
 *
 * MRV is derived per-muscle from getMuscleWeeklyTarget × 1.4 (the standard
 * science-based MRV multiplier over MEV).
 *
 * The phase is determined by the HIGHEST-volume primary muscle group
 * currently trained — i.e., the muscle closest to its MRV sets the phase.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { getMuscleWeeklyTarget, isHardSet } from '@/hooks/useWeeklyVolume';
import type { ExperienceLevel, MuscleGroup, TrainingGoal } from '@/types';

// ── Phase definitions ─────────────────────────────────────────

export interface TrainingPhase {
  phase: 1 | 2 | 3 | 4;
  totalPhases: 4;
  label: string;
  cue: string;
  /** Tailwind text color */
  color: string;
  /** 0–1 representing overall volume saturation */
  saturation: number;
}

const PHASES: Record<1 | 2 | 3 | 4, Omit<TrainingPhase, 'phase' | 'totalPhases' | 'saturation'>> = {
  1: {
    label: 'Foundation',
    cue: 'Focus on form and full range of motion. Sets should feel controlled.',
    color: 'text-fresh',
  },
  2: {
    label: 'Development',
    cue: 'Progressive overload is active. Add small weight when reps feel easy.',
    color: 'text-info',
  },
  3: {
    label: 'Close to Peak Effort',
    cue: "Today's last set should feel like you could do one more rep with control.",
    color: 'text-warning',
  },
  4: {
    label: 'Peak / Deload Soon',
    cue: 'Volume is high — maintain intensity but listen to your body. Deload is near.',
    color: 'text-fatigued',
  },
};

// Primary muscles used to gauge phase (major movers only)
const PHASE_MUSCLES: MuscleGroup[] = ['chest', 'lats', 'quads', 'hamstrings', 'glutes'];

export function useTrainingPhase(): TrainingPhase {
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  const exp  = (settings?.experienceLevel ?? 'intermediate') as ExperienceLevel;
  const goal = (settings?.trainingGoal    ?? 'hypertrophy')  as TrainingGoal;
  const deloadRecommended = settings?.deloadRecommended ?? false;

  const saturation = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 7 * 24 * 3_600_000).toISOString();
    const sets = await db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => isHardSet(s.rpe, s.isWarmup))
      .toArray();

    const exIds = [...new Set(sets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exIds);
    const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    const vol = new Map<MuscleGroup, number>();
    for (const s of sets) {
      const ex = exMap.get(s.exerciseId);
      if (!ex) continue;
      for (const alloc of ex.muscles) {
        vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + alloc.fraction);
      }
    }

    // Find highest saturation among primary muscles
    let maxRatio = 0;
    for (const muscle of PHASE_MUSCLES) {
      const target = getMuscleWeeklyTarget(muscle, exp, goal);
      const mrv    = Math.round(target * 1.4);
      const done   = vol.get(muscle) ?? 0;
      maxRatio = Math.max(maxRatio, done / mrv);
    }
    return Math.min(maxRatio, 1);
  }, [exp, goal]) ?? 0;

  let phase: 1 | 2 | 3 | 4;
  if (deloadRecommended || saturation >= 0.9) {
    phase = 4;
  } else if (saturation >= 0.7) {
    phase = 3;
  } else if (saturation >= 0.4) {
    phase = 2;
  } else {
    phase = 1;
  }

  return {
    phase,
    totalPhases: 4,
    saturation,
    ...PHASES[phase],
  };
}
